/**
 * Badge コンポーネント
 *
 * ジャンルやカテゴリを表示する小さなラベルコンポーネント。
 *
 */

import { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * バッジのバリアント
 */
type BadgeVariant = 'default' | 'outline';

/**
 * Badge コンポーネントのProps
 */
export interface BadgeProps extends HTMLAttributes<HTMLDivElement> {
  /** バッジのバリアント（デフォルト: 'default'） */
  variant?: BadgeVariant;
}

/**
 * バリアントごとのスタイル定義
 */
const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-primary text-primary-foreground',
  outline: 'border border-input bg-background text-foreground',
};

/**
 * Badge コンポーネント
 *
 * 使用例:
 * ```tsx
 * <Badge>デフォルト</Badge>
 * <Badge variant="outline">アニメーション</Badge>
 * ```
 */
export function Badge({
  className,
  variant = 'default',
  children,
  ...props
}: BadgeProps) {
  return (
    <div
      className={cn(
        // 基本スタイル
        'inline-flex items-center rounded-full px-2.5 py-0.5',
        'text-xs font-semibold',
        'transition-colors',
        // バリアント
        variantStyles[variant],
        // カスタムクラス
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}
